import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { SearchableDropdown, Small } from "@fronterahealth/frontera-ui-components";

import { ApiOrganizationOrganizationTypeChoices, UnifiedGoalStatusEnum } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";
import { SortOption } from "@pages/ClinicalReportDetails/SelectProgramming/useSelectProgrammingFilters";
import { useAdminData } from "@providers/AdminDataProvider";

interface SelectProgrammingFilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onDateRangeFilterChange: (ranges: string[]) => void;
  customDateRange: { start: string; end: string };
  onCustomDateRangeChange: (range: { start: string; end: string }) => void;
  onStatusFilterChange: (statuses: string[]) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
}

const dateRangePresets = [
  { id: "30", label: "Last 30 days" },
  { id: "60", label: "Last 60 days" },
  { id: "90", label: "Last 90 days" },
  { id: "180", label: "Last 6 months" },
  { id: "365", label: "Last 12 months" },
  { id: "custom", label: "Custom" },
];

const BASE_STATUSES: UnifiedGoalStatusEnum[] = [
  UnifiedGoalStatusEnum.Inactive,
  UnifiedGoalStatusEnum.Baseline,
  UnifiedGoalStatusEnum.Treatment,
  UnifiedGoalStatusEnum.Mastered,
  UnifiedGoalStatusEnum.Maintenance,
  UnifiedGoalStatusEnum.Generalization,
  UnifiedGoalStatusEnum.Hold,
  UnifiedGoalStatusEnum.Closed,
];

// Required when the enum value we get from the backend doesnt match the display value we want to show in the UI (e.g. CentralReach's "On Hold" status comes as "OnHold")
type StatusOptions = {
  enumValue: UnifiedGoalStatusEnum;
  actualValue: string;
};

const ORG_EXTRA_STATUSES: Partial<
  Record<ApiOrganizationOrganizationTypeChoices, Array<UnifiedGoalStatusEnum | StatusOptions>>
> = {
  [ApiOrganizationOrganizationTypeChoices.HiRasmus]: [
    UnifiedGoalStatusEnum.Waiting,
    UnifiedGoalStatusEnum.Active,
    UnifiedGoalStatusEnum.Discontinued,
  ],
  [ApiOrganizationOrganizationTypeChoices.Motivity]: [
    UnifiedGoalStatusEnum.Active,
    UnifiedGoalStatusEnum.NotIntroduced,
    UnifiedGoalStatusEnum.Completed,
    UnifiedGoalStatusEnum.Deactivated,
    UnifiedGoalStatusEnum.OnHold,
    UnifiedGoalStatusEnum.BaselineAssess,
    UnifiedGoalStatusEnum.InMaintenance,
  ],
  [ApiOrganizationOrganizationTypeChoices.CentralReach]: [
    { enumValue: UnifiedGoalStatusEnum.NotActive, actualValue: "NotActive" },
    UnifiedGoalStatusEnum.Progress,
    { enumValue: UnifiedGoalStatusEnum.OnHold, actualValue: "On Hold" },
    UnifiedGoalStatusEnum.Met,
    UnifiedGoalStatusEnum.Discontinued,
  ],
  [ApiOrganizationOrganizationTypeChoices.HippHealth]: [
    UnifiedGoalStatusEnum.InProgress,
    UnifiedGoalStatusEnum.InMaintenance,
    UnifiedGoalStatusEnum.Archived,
    UnifiedGoalStatusEnum.Discontinued,
    UnifiedGoalStatusEnum.Future,
    UnifiedGoalStatusEnum.OnHold,
  ],
  [ApiOrganizationOrganizationTypeChoices.Rethink]: [
    UnifiedGoalStatusEnum.Active,
    UnifiedGoalStatusEnum.OnHold,
    UnifiedGoalStatusEnum.Mastered,
    UnifiedGoalStatusEnum.Discontinued,
  ],
};

const sortItems: { id: SortOption; primary: string }[] = [
  { id: "last_worked_on", primary: "Last worked on (most recent first)" },
  { id: "status", primary: "Goal status" },
  { id: "alphabetical", primary: "Alphabetical (A-Z)" },
  { id: "recently_updated", primary: "Recently updated" },
];

export const SelectProgrammingFilterBar: React.FC<SelectProgrammingFilterBarProps> = ({
  searchTerm,
  onSearchChange,
  onDateRangeFilterChange,
  customDateRange,
  onCustomDateRangeChange,
  onStatusFilterChange,
  sortBy,
  onSortChange,
}) => {
  const { organizationType } = useAdminData();
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const sortLabelRef = useRef<HTMLSpanElement>(null);
  const [showSortTooltip, setShowSortTooltip] = useState(false);

  const statusItems = useMemo(() => {
    const extra = ORG_EXTRA_STATUSES[organizationType];

    const statuses = extra?.length ? extra : BASE_STATUSES;

    return statuses.map((status) => {
      const enumValue = typeof status === "object" ? status.enumValue : status;
      const actualValue = typeof status === "object" ? status.actualValue : null;
      return {
        id: actualValue || enumValue,
        label: convertReadableString(enumValue),
      };
    });
  }, [organizationType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setDateDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePresetClick = (presetId: string) => {
    if (presetId === "custom") {
      setSelectedPreset("custom");
      onDateRangeFilterChange(["custom"]);
    } else {
      setSelectedPreset(presetId);
      onDateRangeFilterChange([presetId]);
      onCustomDateRangeChange({ start: "", end: "" });
      setDateDropdownOpen(false);
    }
  };

  const handleClearDateRange = () => {
    setSelectedPreset(null);
    onDateRangeFilterChange([]);
    onCustomDateRangeChange({ start: "", end: "" });
    setDateDropdownOpen(false);
  };

  const hasDateFilter = selectedPreset !== null;

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      {/* Search input */}
      <div className="relative min-w-[200px] flex-1 max-w-[400px]">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-icon-secondary">
          <MagnifyingGlassIcon className="w-4 h-4" />
        </div>
        <input
          type="text"
          placeholder="Search goals..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-border-strong rounded-md bg-white focus:ring-1 focus:ring-border-focus focus:border-border-focus h-10"
        />
      </div>

      {/* Date Range filter */}
      <div className="relative" ref={dateDropdownRef}>
        <button
          type="button"
          onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
          aria-haspopup="listbox"
          aria-expanded={dateDropdownOpen}
          className={`flex items-center gap-1.5 h-10 px-3 text-sm border rounded-md bg-white ${
            hasDateFilter ? "border-border-focus text-text-primary" : "border-border-strong text-text-primary"
          }`}
        >
          Date Range
          {hasDateFilter && (
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-interactive-highlight-default text-white text-[10px] font-bold">
              1
            </span>
          )}
          <svg className="w-3 h-3 ml-0.5" fill="none" viewBox="0 0 10 6">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {dateDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-limestone-200 rounded-lg shadow-lg z-20 flex max-w-[calc(100vw-2rem)]">
            {/* Presets list */}
            <div className="py-2 min-w-[160px]">
              {dateRangePresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handlePresetClick(preset.id)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-limestone-50 flex items-center justify-between ${
                    selectedPreset === preset.id ? "font-medium" : ""
                  }`}
                >
                  {preset.label}
                  {selectedPreset === preset.id && (
                    <svg className="w-4 h-4 text-green-600 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {/* Custom date inputs */}
            {selectedPreset === "custom" && (
              <div className="border-l border-limestone-200 p-4 flex flex-col gap-3">
                <div className="flex gap-4">
                  <div>
                    <Small colorType="primary" displayType="loud" className="mb-1 block">
                      Start Date
                    </Small>
                    <input
                      type="date"
                      value={customDateRange.start}
                      onChange={(e) => onCustomDateRangeChange({ ...customDateRange, start: e.target.value })}
                      className="px-3 py-2 text-sm border border-limestone-200 rounded-md bg-white focus:ring-1 focus:ring-hatch-600 focus:border-hatch-600"
                    />
                  </div>
                  <div>
                    <Small colorType="primary" displayType="loud" className="mb-1 block">
                      End date
                    </Small>
                    <input
                      type="date"
                      value={customDateRange.end}
                      onChange={(e) => onCustomDateRangeChange({ ...customDateRange, end: e.target.value })}
                      className="px-3 py-2 text-sm border border-limestone-200 rounded-md bg-white focus:ring-1 focus:ring-hatch-600 focus:border-hatch-600"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearDateRange}
                  className="self-end text-sm text-green-700 font-medium hover:underline"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Goal Status filter */}
      <SearchableDropdown
        label="Goal Status"
        placeholder=""
        items={statusItems}
        onSelectionChange={(selectedItems) => {
          onStatusFilterChange(selectedItems.map((item) => item.id));
        }}
        showSearch={false}
        withHeader={true}
        withClear={true}
        containerClassNames="h-10"
      />

      {/* Sort */}
      <div className="relative shrink-0" ref={sortDropdownRef}>
        <button
          type="button"
          onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
          aria-haspopup="listbox"
          aria-expanded={sortDropdownOpen}
          className="flex items-center gap-1 h-10 px-3 text-sm text-text-primary shrink-0"
        >
          <Small colorType="primary" displayType="normal" className="font-semibold whitespace-nowrap">
            Sort:
          </Small>
          <span
            className="relative w-[130px]"
            onMouseEnter={() => {
              if (sortLabelRef.current && sortLabelRef.current.scrollWidth > sortLabelRef.current.offsetWidth) {
                setShowSortTooltip(true);
              }
            }}
            onMouseLeave={() => setShowSortTooltip(false)}
          >
            <span ref={sortLabelRef} className="block truncate">
              {sortItems.find((s) => s.id === sortBy)?.primary}
            </span>
            {showSortTooltip && (
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-xs text-white bg-gray-900 rounded-sm whitespace-nowrap pointer-events-none z-50">
                {sortItems.find((s) => s.id === sortBy)?.primary}
              </span>
            )}
          </span>
          <svg className="w-3 h-3 ml-0.5 shrink-0" fill="none" viewBox="0 0 10 6">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {sortDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-border-strong rounded-lg shadow-lg z-20 w-[340px] max-w-[calc(100vw-2rem)]">
            {sortItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onSortChange(item.id);
                  setSortDropdownOpen(false);
                }}
                className={`w-full text-left px-5 py-3.5 text-sm flex items-center justify-between hover:bg-limestone-50 ${
                  index < sortItems.length - 1 ? "border-b border-limestone-100" : ""
                }`}
              >
                {item.primary}
                {sortBy === item.id && (
                  <svg
                    className="w-5 h-5 text-green-600 ml-4 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
